import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageSeo from '@/components/base/PageSeo';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { t } = useTranslation();
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const [statusFilter, setStatusFilter] = useState<'all' | 'ongoing' | 'completed'>('all');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = categories.find((c) => c.slug === categorySlug);

  const filteredComics = useMemo(() => {
    let result = categorySlug === 'all' ? [...comics] : [...comics].filter((c) => c.category === categorySlug);

    if (statusFilter === 'ongoing') result = result.filter((c) => c.status === 'ongoing');
    if (statusFilter === 'completed') result = result.filter((c) => c.status === 'completed');

    if (sortBy === 'popular') result.sort((a, b) => b.views - a.views);
    if (sortBy === 'rating') result.sort((a, b) => b.rating - a.rating);
    if (sortBy === 'latest') result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

    return result;
  }, [categorySlug, statusFilter, sortBy]);

  const pageTitle = currentCategory
    ? `${currentCategory.name}韩漫_好看的韩漫漫画推荐_免费韩漫漫画全集_韩漫网`
    : '全部分类韩漫_好看的韩漫漫画推荐_免费韩漫漫画全集_韩漫网';

  const pageDesc = currentCategory
    ? `韩漫网${currentCategory.name}分类专区，精选好看的韩漫漫画推荐，免费韩漫漫画全集在线观看，更新及时，内容丰富。`
    : '韩漫网全部分类页面，精选好看的韩漫漫画推荐，免费韩漫漫画全集在线观看，涵盖恋爱、奇幻、动作等海量类型。';

  return (
    <>
      <PageSeo
        title={pageTitle}
        description={pageDesc}
        keywords={`${currentCategory ? currentCategory.name + '韩漫,' : ''}好看的韩漫漫画推荐,免费韩漫漫画全集,韩漫网`}
        canonicalUrl={`https://hanmanwang.com/category/${categorySlug}`}
        ogTitle={pageTitle}
        ogDescription={pageDesc}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": currentCategory ? `${currentCategory.name}韩漫` : '全部分类韩漫',
            "url": `https://hanmanwang.com/category/${categorySlug}`,
            "description": pageDesc,
            "isPartOf": { "@id": "https://hanmanwang.com/#website" }
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://hanmanwang.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": currentCategory ? `${currentCategory.name}韩漫` : '全部分类韩漫',
                "item": `https://hanmanwang.com/category/${categorySlug}`
              }
            ]
          }
        ]}
      />

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: currentCategory ? currentCategory.name : '全部分类' },
          ]}
        />

        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-2 mb-6">
          {currentCategory ? currentCategory.name : '全部分类'}
          <span className="text-base font-normal text-foreground-500 ml-2">
            {currentCategory ? '韩漫推荐' : '韩漫大全'}
          </span>
        </h1>

        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="lg:w-[240px] flex-shrink-0">
            <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 sticky top-24">
              <h3 className="text-sm font-semibold text-foreground-900 mb-3">{t('category.allCategories')}</h3>
              <div className="flex flex-col gap-1 mb-6">
                <Link
                  to="/category/all"
                  className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                    categorySlug === 'all'
                      ? 'bg-primary-100 text-primary-700 font-medium'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  全部分类
                </Link>
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      categorySlug === cat.slug
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              <h3 className="text-sm font-semibold text-foreground-900 mb-3">{t('category.filterByStatus')}</h3>
              <div className="flex flex-col gap-1 mb-6">
                {[
                  { value: 'all', label: t('category.all') },
                  { value: 'ongoing', label: t('category.ongoing') },
                  { value: 'completed', label: t('category.completed') },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setStatusFilter(opt.value as 'all' | 'ongoing' | 'completed')}
                    className={`px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                      statusFilter === opt.value
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <h3 className="text-sm font-semibold text-foreground-900 mb-3">{t('category.sortBy')}</h3>
              <div className="flex flex-col gap-1">
                {[
                  { value: 'latest', label: t('category.sortLatest') },
                  { value: 'popular', label: t('category.sortPopular') },
                  { value: 'rating', label: t('category.sortRating') },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value as 'latest' | 'popular' | 'rating')}
                    className={`px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                      sortBy === opt.value
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <p className="text-sm text-foreground-500 mb-6">
              {t('category.resultCount', { count: filteredComics.length })}
            </p>

            {filteredComics.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
                <p className="text-foreground-500 mt-4">{t('category.noResults')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {filteredComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.slug}`}
                    className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 left-2">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            comic.status === 'ongoing'
                              ? 'bg-primary-500 text-background-50'
                              : 'bg-secondary-500 text-background-50'
                          }`}
                        >
                          {comic.status === 'ongoing' ? t('home.statusOngoing') : t('home.statusCompleted')}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <div className="flex items-center gap-2 text-background-50 text-xs">
                          <span className="flex items-center gap-1">
                            <i className="ri-star-fill text-accent-500 text-xs"></i>
                            {comic.rating}
                          </span>
                          <span className="opacity-60">
                            {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">
                        {comic.author} · {t('home.updatedAt')} {comic.updatedAt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}